<template>
    <div class="app">
        <DefaultHeader @change-password="$bvModal.show('modal-change-password')" @change-passcode="$bvModal.show('modal-change-passcode')" @logout="logout" :fullName="fullName" :roleName="roleName" />
        <div class="app-body">
            <AppSidebar fixed>
                <SidebarHeader/>
                <div class="sidebar-header-c">
                    <div class="show-on-mobile" style="margin: 1em">
                        <div style="line-height: 1.2">{{ fullName }}</div>
                        <div class="small font-weight-bold">{{ roleName }}</div>
                    </div>
                </div>
                <div class="sidebar-search" v-click-outside="closeSuggestions">
                    <div class="sidebar-search-input">
                        <i class="fa fa-search"></i>
                        <input
                            type="text"
                            v-model="searchQuery"
                            placeholder="Cari menu..."
                            @focus="searchFocused = true"
                            @keydown.esc="closeSuggestions"
                            @keydown.down.prevent="highlightNext"
                            @keydown.up.prevent="highlightPrev"
                            @keydown.enter.prevent="goToHighlighted"
                            ref="searchInput"
                        />
                        <i
                            v-if="searchQuery"
                            class="fa fa-times sidebar-search-clear"
                            @click="searchQuery = ''; searchFocused = false"
                        ></i>
                    </div>
                    <ul class="sidebar-search-results" v-if="searchQuery && searchFocused && searchResults.length > 0">
                        <li
                            v-for="(item, index) in searchResults"
                            :key="item.url"
                            :class="{ highlighted: index === highlightedIndex }"
                            @mousedown.prevent="goTo(item.url)"
                            @mouseenter="highlightedIndex = index"
                        >
                            <i :class="item.icon"></i>
                            <div class="sidebar-search-item-text">
                                <span class="sidebar-search-item-name">{{ item.name }}</span>
                                <span class="sidebar-search-item-parent" v-if="item.parentName">{{ item.parentName }}</span>
                            </div>
                        </li>
                    </ul>
                    <div class="sidebar-search-empty" v-if="searchQuery && searchFocused && searchResults.length === 0">
                        Tidak ditemukan
                    </div>
                </div>
                <SidebarNav :navItems="nav"></SidebarNav>
            </AppSidebar>
            <main class="main">
                <CustomBreadcrumb :list="list"/>
                <div class="container-fluid">
                <router-view></router-view>
                </div>
            </main>
        </div>
        <DefaultFooter/>
        <b-modal id="modal-change-password" title="Ubah Password"
            centered no-close-on-backdrop no-close-on-esc hide-footer
            @shown="$refs.input.focus()" @hidden="onHideChangePasswordModal"
        >
            <b-form-group label="Password Lama" class="password-form-group" style="max-width: none">
                <b-form-input ref="input" :type="input.opType" v-model="input.oldPassword" />
                <i :class="input.opType == 'password' ? 'fa fa-eye-slash' : 'fa fa-eye'" @click="changePasswordType('opType')" />
            </b-form-group>
            <b-form-group label="Password Baru" class="password-form-group" style="max-width: none">
                <b-form-input :type="input.npType" v-model="input.newPassword" />
                <i :class="input.npType == 'password' ? 'fa fa-eye-slash' : 'fa fa-eye'" @click="changePasswordType('npType')" />
            </b-form-group>
            <b-form-group label="Ulangi Password Baru" class="password-form-group" style="max-width: none">
                <b-form-input :type="input.cpType" v-model="input.confirmNewPassword" />
                <i :class="input.cpType == 'password' ? 'fa fa-eye-slash' : 'fa fa-eye'" @click="changePasswordType('cpType')" />
            </b-form-group>
            <div class="text-right">
                <b-btn variant="primary" :disabled="isSubmitting" @click="executeChangePassword">
                    <b-spinner small type="grow" v-if="isSubmitting" />
                    {{ isSubmitting ? 'Menyimpan...' : 'Simpan' }}
                </b-btn>
            </div>
        </b-modal>
        <b-modal id="modal-change-passcode" title="Ubah Passcode"
            centered no-close-on-backdrop no-close-on-esc hide-footer
            @shown="$refs.inputPasscode.focus()" @hidden="onHideChangePasscodeModal"
        >
            <b-form-group label="Passcode Lama" class="password-form-group" style="max-width: none">
                <b-form-input ref="inputPasscode" :type="inputPasscode.opType" v-model="inputPasscode.oldPasscode" maxlength="6" @keypress="numberOnly" />
                <i :class="inputPasscode.opType == 'password' ? 'fa fa-eye-slash' : 'fa fa-eye'" @click="changePasscodeType('opType')" />
            </b-form-group>
            <b-form-group label="Passcode Baru" class="password-form-group" style="max-width: none">
                <b-form-input :type="inputPasscode.npType" v-model="inputPasscode.newPasscode" maxlength="6" @keypress="numberOnly" />
                <i :class="inputPasscode.npType == 'password' ? 'fa fa-eye-slash' : 'fa fa-eye'" @click="changePasscodeType('npType')" />
            </b-form-group>
            <b-form-group label="Ulangi Passcode Baru" class="password-form-group" style="max-width: none">
                <b-form-input :type="inputPasscode.cpType" v-model="inputPasscode.confirmNewPasscode" maxlength="6" @keypress="numberOnly" />
                <i :class="inputPasscode.cpType == 'password' ? 'fa fa-eye-slash' : 'fa fa-eye'" @click="changePasscodeType('cpType')" />
            </b-form-group>
            <div class="text-right">
                <b-btn variant="primary" :disabled="isSubmitting" @click="executeChangePasscode">
                    <b-spinner small type="grow" v-if="isSubmitting" />
                    {{ isSubmitting ? 'Menyimpan...' : 'Simpan' }}
                </b-btn>
            </div>
        </b-modal>
    </div>
</template>

<script>
import nav from '@/nav'
import { Sidebar as AppSidebar, SidebarHeader, SidebarNav } from '@coreui/vue'
import DefaultHeaderDropdownAccnt from './DefaultHeaderDropdownAccnt'
import DefaultHeader from './DefaultHeader'
import DefaultFooter from './DefaultFooter'
import CustomBreadcrumb from './CustomBreadcrumb'

export default {
    name: 'DefaultContainer',
    directives: {
        'click-outside': {
            bind(el, binding) {
                el._clickOutsideHandler = (e) => {
                    if (!el.contains(e.target)) binding.value()
                }
                document.addEventListener('mousedown', el._clickOutsideHandler)
            },
            unbind(el) {
                document.removeEventListener('mousedown', el._clickOutsideHandler)
            }
        }
    },
    components: {
        AppSidebar,
        CustomBreadcrumb,
        DefaultHeaderDropdownAccnt,
        SidebarHeader,
        SidebarNav,
        DefaultFooter,
        DefaultHeader
    },
    data () {
        return {

            nav: [],

            fullName: "",
            roleName: "",

            isSubmitting: false,

            searchQuery: "",
            searchFocused: false,
            highlightedIndex: -1,

            input: {
                oldPassword: "",
                newPassword: "",
                confirmNewPassword: "",
                opType: "password",
                npType: "password",
                cpType: "password"
            },

            inputPasscode: {
                oldPasscode: "",
                newPasscode: "",
                confirmNewPasscode: "",
                opType: "password",
                npType: "password",
                cpType: "password"
            },

        }
    },
    computed: {

        name () {
            return this.$route.name
        },

        list () {
            return this.$route.matched.filter((route) => (route.name || route.meta.label) && !route.meta.hideInBreadcrumb)
        },

        flatNavItems () {
            const items = []
            this.nav.forEach(item => {
                if (item.children) {
                    item.children.forEach(child => {
                        items.push({
                            name: child.name,
                            url: child.url,
                            icon: child.icon,
                            parentName: item.name
                        })
                    })
                } else {
                    items.push({
                        name: item.name,
                        url: item.url,
                        icon: item.icon,
                        parentName: null
                    })
                }
            })
            return items
        },

        searchResults () {
            if (!this.searchQuery) return []
            const q = this.searchQuery.toLowerCase()
            return this.flatNavItems
                .filter(item => item.name.toLowerCase().includes(q))
                .slice(0, 8)
        }

    },
    watch: {
        searchQuery () {
            this.highlightedIndex = -1
        }
    },
    methods: {

        goTo(url) {
            this.searchQuery = ""
            this.searchFocused = false
            this.highlightedIndex = -1
            if (this.$refs.searchInput) this.$refs.searchInput.blur()
            if (this.$route.path !== url) this.$router.push(url)
        },

        numberOnly(event) {
            if (
                (event.charCode >= 48 && event.charCode <= 57) ||
                event.charCode == 13
            ) {
                return true;
            } else {
                event.preventDefault();
            }
        },

        closeSuggestions() {
            this.searchFocused = false
            this.highlightedIndex = -1
        },

        highlightNext() {
            if (this.searchResults.length === 0) return
            this.highlightedIndex = (this.highlightedIndex + 1) % this.searchResults.length
        },

        highlightPrev() {
            if (this.searchResults.length === 0) return
            this.highlightedIndex = this.highlightedIndex <= 0
                ? this.searchResults.length - 1
                : this.highlightedIndex - 1
        },

        goToHighlighted() {
            if (this.highlightedIndex >= 0 && this.highlightedIndex < this.searchResults.length) {
                this.goTo(this.searchResults[this.highlightedIndex].url)
            }
        },

        executeChangePassword() {

            if(this.$helper.isBlank(this.input.oldPassword)) {
                return this.$helper.toastError(this, "Password lama tidak boleh kosong!")
            }

            if(this.$helper.isBlank(this.input.newPassword)) {
                return this.$helper.toastError(this, "Password baru tidak boleh kosong!")
            }

            if(this.$helper.isBlank(this.input.confirmNewPassword)) {
                return this.$helper.toastError(this, "Ulangi password baru tidak boleh kosong!")
            }

            if(this.input.newPassword != this.input.confirmNewPassword) {
                return this.$helper.toastError(this, "Password baru dan ulangi password baru tidak sama!")
            }

            let payload = {
                current: this.input.oldPassword,
                new: this.input.newPassword,
                repeat: this.input.confirmNewPassword
            }

            this.isSubmitting = true
            this.$axios.post("user/change-password", payload).then(res => {

                this.$bvModal.hide("modal-change-password")
                this.$helper.toastSuccess(this, "Password berhasil diubah!")
                this.onHideChangePasswordModal()

            })
            .catch(error => { this.$helper.parseError(this, error) })
            .finally(() => { this.isSubmitting = false })

        },

        changePasswordType(section) {

            if(this.input[section] == "password") {
                this.input[section] = "text"
            }
            else {
                this.input[section] = "password"
            }

        },

        onHideChangePasswordModal() {

            this.input.oldPassword = ""
            this.input.newPassword = ""
            this.input.confirmNewPassword = ""
            this.input.opType = "password"
            this.input.npType = "password"
            this.input.cpType = "password"

        },

        executeChangePasscode() {

            if(this.$helper.isBlank(this.inputPasscode.oldPasscode)) {
                return this.$helper.toastError(this, "Passcode lama tidak boleh kosong!")
            }

            if(this.$helper.isBlank(this.inputPasscode.newPasscode)) {
                return this.$helper.toastError(this, "Passcode baru tidak boleh kosong!")
            }

            if(this.$helper.isBlank(this.inputPasscode.confirmNewPasscode)) {
                return this.$helper.toastError(this, "Ulangi passcode baru tidak boleh kosong!")
            }

            if(this.inputPasscode.newPasscode != this.inputPasscode.confirmNewPasscode) {
                return this.$helper.toastError(this, "Passcode baru dan ulangi passcode baru tidak sama!")
            }

            let payload = {
                current: this.inputPasscode.oldPasscode,
                new: this.inputPasscode.newPasscode,
                repeat: this.inputPasscode.confirmNewPasscode
            }

            this.isSubmitting = true
            this.$axios.post("user/change-passcode", payload).then(res => {

                this.$bvModal.hide("modal-change-passcode")
                this.$helper.toastSuccess(this, "Passcode berhasil diubah!")
                this.onHideChangePasscodeModal()

            })
            .catch(error => { this.$helper.parseError(this, error) })
            .finally(() => { this.isSubmitting = false })

        },

        changePasscodeType(section) {

            if(this.inputPasscode[section] == "password") {
                this.inputPasscode[section] = "text"
            }
            else {
                this.inputPasscode[section] = "password"
            }

        },

        onHideChangePasscodeModal() {

            this.inputPasscode.oldPasscode = ""
            this.inputPasscode.newPasscode = ""
            this.inputPasscode.confirmNewPasscode = ""
            this.inputPasscode.opType = "password"
            this.inputPasscode.npType = "password"
            this.inputPasscode.cpType = "password"

        },

        logout() {

            var version = localStorage.getItem("version")
            localStorage.clear()
            sessionStorage.clear()
            localStorage.setItem("", version)

            this.$router.push("/login")

        }

    },
    created() {

        // Auto-redirect to login page if token does not exist --> moved to router
        if(localStorage.getItem("token") == null) {
            this.$router.push({ path: "/login" })
            return
        }

        this.$axios.get("user/check-token").then(res => {
            if (res.data && res.data.data && res.data.data.hasPasscode !== undefined) {
                localStorage.setItem("hasPasscode", res.data.data.hasPasscode)
            }
        })

        this.fullName = localStorage.getItem("fullname")
        this.roleName = localStorage.getItem("roleName")

        // Consolidate navigation properties
        let navs = JSON.parse(JSON.stringify(nav.items))
        let permissions = localStorage.getItem("permissions").split(",").map(it => Number(it))
        // console.log(permissions)

        if(localStorage.getItem("roleId") != 1) { // Bukan superadmin, permission-based

            let newNav = []
            navs.forEach(item => {

                if(!item.hasOwnProperty("children") && (item.permission == null || permissions.includes(item.permission))) {
                    newNav.push(item)
                }

                if(item.hasOwnProperty("children")) {

                    newNav.push(item)

                    let children = [];
                    item.children.forEach(it => {
                        if(it.permission == null || permissions.includes(it.permission)) {
                            children.push(it)
                        }
                    })

                    item.children = children

                    if(children.length == 0) {
                        newNav.pop();
                    }

                }

            })

            this.nav = newNav

        }
        else {
            this.nav = navs
        }

    },
    mounted() {

        // Check versioning. Hard refresh if reload is required
        let reloadStatus = localStorage.getItem("reload")

        if(reloadStatus != null && reloadStatus == 1) {
            localStorage.removeItem("reload")
            window.location.reload()
        }

    }
}
</script>

<style>
.sidebar .sidebar-search {
    position: relative;
    padding: 12px 15px;
    background-color: #042b6b;
}

.sidebar .sidebar-search-input {
    position: relative;
    display: flex;
    align-items: center;
    background-color: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 4px;
    padding: 0 10px;
    transition: border-color 0.2s, background-color 0.2s;
}

.sidebar .sidebar-search-input:focus-within {
    border-color: rgba(255, 255, 255, 0.4);
    background-color: rgba(255, 255, 255, 0.15);
}

.sidebar .sidebar-search-input > i.fa-search {
    color: rgba(255, 255, 255, 0.5);
    font-size: 13px;
    flex-shrink: 0;
}

.sidebar .sidebar-search-input input {
    background: transparent;
    border: none;
    outline: none;
    color: #fff;
    padding: 7px 8px;
    font-size: 13px;
    width: 100%;
}

.sidebar .sidebar-search-input input::placeholder {
    color: rgba(255, 255, 255, 0.45);
}

.sidebar .sidebar-search-clear {
    color: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    font-size: 12px;
    flex-shrink: 0;
    padding: 4px;
}

.sidebar .sidebar-search-clear:hover {
    color: #fff;
}

.sidebar ul.sidebar-search-results {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: #0d3d8a;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-top: none;
    list-style: none !important;
    margin: 0 !important;
    padding: 4px 0 !important;
    z-index: 1050;
    max-height: 320px;
    overflow-y: auto;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    border-radius: 0 0 4px 4px;
}

.sidebar ul.sidebar-search-results li {
    display: flex !important;
    align-items: center !important;
    padding: 8px 15px !important;
    margin: 0 !important;
    cursor: pointer;
    transition: background-color 0.15s;
    gap: 10px;
    list-style: none !important;
    background: transparent;
    border: none;
}

.sidebar ul.sidebar-search-results li:hover,
.sidebar ul.sidebar-search-results li.highlighted {
    background-color: #3173df !important;
}

.sidebar ul.sidebar-search-results li > i {
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
    width: 20px;
    text-align: center;
    flex-shrink: 0;
}

.sidebar .sidebar-search-item-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
}

.sidebar .sidebar-search-item-name {
    color: #fff;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.sidebar .sidebar-search-item-parent {
    color: rgba(255, 255, 255, 0.45);
    font-size: 11px;
    line-height: 1.3;
    display: block;
}

.sidebar .sidebar-search-empty {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: #0d3d8a;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-top: none;
    padding: 10px 15px;
    color: rgba(255, 255, 255, 0.45);
    font-size: 13px;
    z-index: 1050;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    border-radius: 0 0 4px 4px;
}
</style>
